import React, { useState, useEffect, useRef } from 'react';
import { FaSearch } from 'react-icons/fa';
import { Dropdown } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import { LuBell } from "react-icons/lu";
import { LuSearch } from "react-icons/lu";
import { RiDeleteBinLine } from "react-icons/ri";
import { MdOutlineNotificationsActive, MdOutlineNotificationsNone } from "react-icons/md";
import * as apicall from '../Apicall';
import { TbLogout } from "react-icons/tb";
import utilstopLogo from '../../assets/images/Utilstoplogo.svg';
import * as constants from '../../constants/Constants';

const Navbar = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [deletedNotif, setdeletedNotif] = useState([]);
  const bellRef = useRef(null);
  const vendorId = localStorage.getItem('vendorId');
  const [vendorLogo, setVendorLogo] = useState(null);
  const pollingInterval = useRef(null);
  const [hoveredDelete, setHoveredDelete] = useState(null);

  // Store read status in localStorage to persist between refreshes
  const [readNotifications, setReadNotifications] = useState(() => {
    const saved = localStorage.getItem('readNotifications');
    return saved ? JSON.parse(saved) : [];
  });

  // Store deleted notifications to prevent showing them again
  const [deletedNotifications, setDeletedNotifications] = useState(() => {
    const saved = localStorage.getItem('deletedNotifications');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('readNotifications', JSON.stringify(readNotifications));
    localStorage.setItem('deletedNotifications', JSON.stringify(deletedNotifications));
  }, [readNotifications, deletedNotifications]);

  // Add polling mechanism
  useEffect(() => {
    const startPolling = () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
      pollingInterval.current = setInterval(() => {
        if (vendorId) {
          fetchNotifications(vendorId);
        }
      }, 30000); 
    };

    startPolling();

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [vendorId]);

  useEffect(() => {
    const fetchVendorProfile = async () => {
      try {
        const response = await apicall.get('myProfile');
        if (response.status === 200 && response.success) {
          const vendorData = response.data[0];
          localStorage.setItem('vendorId', vendorData.vendor_id); 
          setVendorName(vendorData.first_name);
          setVendorLogo(vendorData.vendor_logo);
          // Initial fetch of notifications
          fetchNotifications(vendorData.vendor_id);
        }
      } catch (error) {
        console.error('Error fetching vendor profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVendorProfile();
  }, []);

  const fetchNotifications = async (vendorId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        constants.apiUrl + 'api/v1/notificationsVendor/' + vendorId,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        // Do NOT refresh token, just return empty
        return [];
      }

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      if (data.status === 200 && data.success) {
        // Filter out deleted notifications and merge with local read status
        const updatedNotifications = data.data
          .filter(notif => !deletedNotifications.includes(notif.id))
          .map(notif => ({
            ...notif,
            is_read: readNotifications.includes(notif.id) ? 1 : notif.is_read
          }));

        // Only update if there are changes
        if (JSON.stringify(notifications) !== JSON.stringify(updatedNotifications)) {
          setNotifications(updatedNotifications);
          setUnreadCount(updatedNotifications.filter(n => n.is_read === 0).length);
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markNotificationAsRead = async (id) => {
    try {
      // Optimistically update UI first
      setNotifications(prev => prev.map(n => 
        n.id === id ? {...n, is_read: 1} : n
      ));
      setReadNotifications(prev => [...prev, id]);
      setUnreadCount(prev => prev > 0 ? prev - 1 : 0);

      // Then update the API
      await apicall.get(`notificationVendor_readStatus/${id}`);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Rollback if API call fails
      setNotifications(prev => prev.map(n => 
        n.id === id ? {...n, is_read: 0} : n
      ));
      setReadNotifications(prev => prev.filter(notifId => notifId !== id));
      setUnreadCount(prev => prev + 1);
    }
  };

  const deleteNotification = async (id) => {
    try {
      // Optimistically update UI first
      const deletedNotif = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setDeletedNotifications(prev => [...prev, id]);
      
      if (deletedNotif && deletedNotif.is_read === 0) {
        setUnreadCount(prev => prev > 0 ? prev - 1 : 0);
      }

      // Then update the API
      await apicall.get(`notificationVendor_delete/${id}`);
    } catch (error) {
      console.error('Error deleting notification:', error);
      // Rollback if API call fails
      if (deletedNotif) {
        setNotifications(prev => [...prev, deletedNotif]);
        setDeletedNotifications(prev => prev.filter(notifId => notifId !== id));
        if (deletedNotif.is_read === 0) {
          setUnreadCount(prev => prev + 1);
        }
      }
    }
  };

  const deleteAllNotifications = async () => {
    try {
      // Optimistically update UI first
      const unreadToDelete = notifications.filter(n => n.is_read === 0).length;
      const allIds = notifications.map(n => n.id);
      
      setNotifications([]);
      setDeletedNotifications(prev => [...prev, ...allIds]);
      setUnreadCount(prev => prev - unreadToDelete > 0 ? prev - unreadToDelete : 0);

      // Then update the API
      await Promise.all(
        notifications.map(notif => 
          apicall.get(`notificationVendor_delete/${notif.id}`)
      ));
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      // Full rollback is complex here, might want to just refresh notifications
      fetchNotifications(vendorId);
    }
  };

  // Close popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    }
    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate('/doctors', { state: { searchQuery: searchQuery.trim() } });
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  return (
    <nav className="navbar navbar-expand-lg navbar-light bg-white fixed-top custom-border-bottom py-2">
      <div className="container-fluid px-3 px-lg-4">
        <div className="d-flex align-items-center">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center me-2"
            style={{
              width: '32px',
              height: '32px',
              overflow: 'hidden',
              backgroundColor: vendorLogo ? 'transparent' : '#1D4ED8'
            }}
          >
            {vendorLogo ? (
              <img
                src={vendorLogo}
                alt={vendorName}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span className="text-white fw-bold">{vendorName?.charAt(0) || ''}</span>
            )}
          </div>
          <div>
            <span className="fw-bold" style={{ fontSize: '16px' }}>{vendorName}</span>
            <div className="text-muted d-flex align-items-center" style={{ fontSize: '12px' }}>
              <span>By</span>
              <img
                src={utilstopLogo}
                alt="UtilStop"
                style={{ height: '16px', marginLeft: '4px' }}
              />
            </div>
          </div>
        </div>


        {/* Right Side Items */}
        <div className="d-flex align-items-center gap-4 ms-auto">
          {/* Search Bar */}
          <div className="d-none d-md-block navbar-search-bar">
            <div
              className="position-relative"
              style={{
                width:
                  typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth <= 1024
                    ? (showSearch ? '200px' : '44px')
                    : (showSearch ? '320px' : '44px')
              }}
            >
              <LuSearch
                onClick={() => setShowSearch(prev => !prev)}
                className="position-absolute"
                style={{
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#000',
                  cursor: 'pointer',
                  zIndex: 2
                }}
              />
              <input
                type="text"
                className={`form-control ps-5 ${showSearch ? '' : 'd-none'}`}
                placeholder="Search for doctors"
                value={searchQuery}
                onChange={handleSearch}
                onKeyDown={handleSearchSubmit}
                autoFocus={showSearch}
                style={{
                  borderRadius: '8px',
                  height: '44px',
                  backgroundColor: '#F5F5F5',
                  border: 'none',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          <div className="position-relative" ref={bellRef}>
            <LuBell
              style={{ fontSize: '20px', color: '#000', cursor: 'pointer' }}
              onClick={() => setShowNotifications((prev) => !prev)}
            />
            {unreadCount > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                style={{ fontSize: '10px', padding: '4px 6px' }}>
                {unreadCount}
              </span>
            )}
            {showNotifications && (
              <div
                style={{
                  position: 'fixed',
                  top: "63px",
                  right: 0,
                  width: '420px',
                  maxHeight: 'calc(100vh - 63px)',
                  background: '#fff',
                  borderLeft: '1px solid #E3EBF8',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  zIndex: 2000,
                  overflowY: 'auto',
                  padding: '16px'
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontWeight: 600 }}>Notifications</span>
                  {notifications.length > 0 && (
                    <button
                      className="btn btn-link p-0"
                      style={{ fontSize: '13px', color: '#1D4ED8', textDecoration: 'none' }}
                      onClick={deleteAllNotifications}
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                {notifications.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#888', marginTop: '40px' }}>
                    You are yet to receive notifications.
                  </div>
                ) : (
                  <div>
                    {notifications.map((notif) => (
                      <div 
                        key={notif.id} 
                        style={{ 
                          borderBottom: '1px solid #eee', 
                          padding: '12px 0',
                          cursor: 'pointer',
                          backgroundColor: notif.is_read === 0 ? '#f8f9fa' : 'transparent',
                          position: 'relative'
                        }}
                        onClick={() => {
                          if (notif.is_read === 0) {
                            markNotificationAsRead(notif.id);
                          }
                          // Add navigation based on redirection if needed
                          // navigate(`/${notif.redirection}/${notif.reference_id}`);
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <div style={{  paddingLeft: '10px',marginRight: '10px' }}>
                            {notif.is_read === 0 ? (
                              <MdOutlineNotificationsActive style={{ color: '#C9194A', fontSize: '20px' }} />
                            ) : (
                              <MdOutlineNotificationsNone style={{ color: '#888', fontSize: '20px' }} />
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600 }}>{notif.title}</div>
                            <div style={{ fontSize: '12px', color: '#555', paddingRight: '10px' }}>
                              {notif.message}
                            </div>

                            <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
                              {new Date(notif.created).toLocaleString()}
                            </div>
                          </div>
                          <button
                            className="btn btn-link p-0"
                            style={{
                              color: hoveredDelete === notif.id ? 'red' : '#dc3545',
                              position: 'absolute',
                              right: '10px',
                              top: '0px',
                              transition: 'color 0.2s ease',
                            }}
                            onMouseEnter={() => setHoveredDelete(notif.id)}
                            onMouseLeave={() => setHoveredDelete(null)}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notif.id);
                            }}
                          >
                            <RiDeleteBinLine style={{ 
                              fontSize: '18px',
                              transform: hoveredDelete === notif.id ? 'scale(1.1)' : 'scale(1)',
                              transition: 'transform 0.2s ease',
                            }} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <Dropdown align="end" className="d-flex align-items-center">
            <Dropdown.Toggle
              variant="link"
              className="p-0 border-0 bg-transparent d-flex align-items-center custom-dropdown-toggle"
              id="dropdown-user"
            >
              <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                style={{ width: '40px', height: '40px', backgroundColor: '#1D4ED8' }}>
                <span className="text-white" style={{ fontSize: '16px' }}>{vendorName.charAt(0)}</span>
              </div>
            </Dropdown.Toggle>

            <style jsx="true">{`
              .custom-dropdown-toggle::after {
                display: none !important;
              }
            `}</style>

            <Dropdown.Menu style={{ marginTop: '8px' }}>
              <Dropdown.Item onClick={handleLogout} className="d-flex align-items-center gap-2">
                <TbLogout style={{ color: '#E70000', fontSize: '24px' }} />
                <span className="form-labels" style={{ color: '#E70000'}}>Sign-Out</span>
              </Dropdown.Item>
            </Dropdown.Menu>


          </Dropdown>
        </div>
      </div>

      {/* Mobile Search */}
      <div className="container-fluid px-3 d-md-none mt-2">
        <div className="position-relative" style={{ maxWidth: '320px', width: '100%' }}>
          <FaSearch className="position-absolute" style={{ left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#8C8C8C' }} />
          <input
            type="text"
            className="form-control ps-5"
            placeholder="Search for doctors"
            value={searchQuery}
            onChange={handleSearch}
            onKeyDown={handleSearchSubmit}
            style={{
              borderRadius: '8px',
              height: '44px',
              backgroundColor: '#F5F5F5',
              border: 'none',
              fontSize: '14px'
            }}
          />
        </div>
      </div>
    </nav>
  );
};

export default Navbar;

